import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, put, del } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, width: '100%' } as React.CSSProperties,
};

const TYPE_NAMES: Record<string, string> = { policy: t('政策'), faq: 'FAQ', voice: t('品牌声音'), product_info: t('产品知识') };

// ═══ 知识条目 ═══
function EntriesView() {
  const [rows, setRows] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [editing, setEditing] = useState<any>(null);

  const load = useCallback(() => {
    get('/api/v1/kb/entries').then((r: any) => setRows(r.data || [])).catch(() => {});
    get('/api/v1/kb/stats').then((r: any) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!editing.title || !editing.content) return alert('标题与内容必填');
    try {
      if (editing.id) await put(`/api/v1/kb/entries/${editing.id}`, editing);
      else await post('/api/v1/kb/entries', editing);
      setEditing(null); load();
    } catch (e: any) { alert(e.message); }
  };

  const open = async (id: string) => {
    try { const r: any = await get(`/api/v1/kb/entries/${id}`); setEditing(r.data); } catch (e: any) { alert(e.message); }
  };

  return (
    <>
      {stats && (
        <div style={S.grid}>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('知识条目')}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{stats.entries}</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('向量分块')}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{stats.chunks}</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('待补缺口')}</div><div style={{ fontSize: 24, fontWeight: 700, color: stats.open_gaps > 0 ? '#d93025' : '#188038' }}>{stats.open_gaps}</div></div>
        </div>
      )}
      {editing ? (
        <div style={S.card}>
          <h3 style={{ fontSize: 15, marginBottom: 12 }}>{editing.id ? '编辑条目' : t('新建条目')}</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <select style={{ ...S.input, width: 140 }} value={editing.entry_type} onChange={e => setEditing({ ...editing, entry_type: e.target.value })}>
              {Object.entries(TYPE_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input style={S.input} placeholder={t('标题')} value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} />
          </div>
          <textarea style={{ ...S.input, height: 220, marginBottom: 10 }} placeholder={t('内容（保存后自动向量化）')} value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btnPrimary} onClick={save}>{t('保存并索引')}</button>
            <button style={S.btn} onClick={() => setEditing(null)}>{t('取消')}</button>
          </div>
        </div>
      ) : (
        <div style={{ ...S.card, display: 'flex', gap: 10 }}>
          <button style={S.btnPrimary} onClick={() => setEditing({ entry_type: 'faq', title: '', content: '', tags: '' })}>{t('＋ 新建知识条目')}</button>
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('类型')}</th><th style={S.th}>{t('标题')}</th><th style={S.th}>{t('更新时间')}</th><th style={S.th}></th></tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id}>
                <td style={S.td}>{TYPE_NAMES[r.entry_type] || r.entry_type}</td>
                <td style={{ ...S.td, cursor: 'pointer', color: '#1a73e8' }} onClick={() => open(r.id)}>{r.title}</td>
                <td style={S.td}>{(r.updated_at || '').slice(0, 16)}</td>
                <td style={S.td}><button style={S.btn} onClick={async () => { if (confirm(t('归档此条目？'))) { await del(`/api/v1/kb/entries/${r.id}`); load(); } }}>{t('归档')}</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={S.td} colSpan={4}>{t('暂无条目 — 添加政策、FAQ、品牌声音指南')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ RAG 测试 ═══
function QueryView() {
  const [q, setQ] = useState('');
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    if (!q) return;
    setBusy(true);
    try { const r: any = await post('/api/v1/kb/query', { question: q }); setResult(r.data); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('知识库问答测试（与其他 App 调用的 API 相同）')}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={S.input} placeholder={t('如：你们的退货政策是什么？')} value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && ask()} />
          <button style={S.btnPrimary} disabled={busy} onClick={ask}>{busy ? '检索中...' : t('提问')}</button>
        </div>
      </div>
      {result && (
        <>
          <div style={{ ...S.card, background: result.matched ? '#e6f4ea' : '#fce8e6' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
              {result.matched ? `✅ 匹配成功（最高分 ${result.best_score}）` : `❌ 未找到匹配知识（已记入缺口，最高分 ${result.best_score}）`}
            </div>
            {result.answer && <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{result.answer}</div>}
          </div>
          <div style={S.card}>
            <h4 style={{ fontSize: 13, marginBottom: 8 }}>{t('检索到的知识块')}</h4>
            {result.chunks.map((ch: any, i: number) => (
              <div key={i} style={{ borderLeft: '3px solid #1a73e8', paddingLeft: 10, marginBottom: 10, fontSize: 12 }}>
                <div style={{ color: '#888' }}>{ch.title}（{TYPE_NAMES[ch.type] || ch.type}）· 相似度 {ch.score}</div>
                <div>{ch.text}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ═══ 知识缺口 ═══
function GapsView() {
  const [rows, setRows] = useState<any[]>([]);

  const load = () => get('/api/v1/gaps').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const resolve = async (id: string, status: string) => {
    try { await post(`/api/v1/gaps/${id}/resolve`, { status }); load(); } catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('知识缺口（客户问了但知识库答不上的问题）')}</h3>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('问题')}</th><th style={S.th}>{t('来源')}</th><th style={S.th}>{t('匹配度')}</th><th style={S.th}>{t('时间')}</th><th style={S.th}>{t('处理')}</th></tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={S.td}>{r.question}</td>
              <td style={S.td}>{r.source_channel}</td>
              <td style={{ ...S.td, color: '#d93025' }}>{Math.round((r.matched_score || 0) * 100)}%</td>
              <td style={S.td}>{(r.created_at || '').slice(0, 16)}</td>
              <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <button style={S.btnPrimary} onClick={() => resolve(r.id, 'covered')}>{t('已补充')}</button>
                  <button style={S.btn} onClick={() => resolve(r.id, 'dismissed')}>{t('忽略')}</button>
                </span>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无缺口 🎉')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 主应用 ═══
function App() {
  const [tab, setTab] = useState('entries');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>BrandKnowledgeHub</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('多渠道知识库与品牌声音 — 结构化知识 · RAG 检索 · 缺口监测')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'entries')} onClick={() => setTab('entries')}>{t('知识管理')}</div>
        <div style={S.tab(tab === 'query')} onClick={() => setTab('query')}>{t('问答测试')}</div>
        <div style={S.tab(tab === 'gaps')} onClick={() => setTab('gaps')}>{t('知识缺口')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'entries' && <EntriesView />}
      {tab === 'query' && <QueryView />}
      {tab === 'gaps' && <GapsView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
